import { useCallback, useState } from 'react';

import { classifyActivityRowTransition } from './activity-feed-viewport';

const NO_IDS: ReadonlySet<string> = new Set();

interface RowSnapshot {
  /** No newer page was left to load, so whatever lands at the end is new activity. */
  atNewest: boolean;
  rowIds: string[];
}

/**
 * Rows that just landed at the end of a feed already showing its newest page: a comment the reader
 * posted, or one arriving live. A newer page loaded by scrolling is not an arrival, so a feed that
 * still had pages to load never animates.
 */
export function useArrivingRowIds({
  enabled,
  hasMore,
  rowIds,
}: {
  enabled: boolean;
  hasMore: boolean;
  rowIds: string[];
}): { arrivingIds: ReadonlySet<string>; onArrived: (id: string) => void } {
  const [arrivingIds, setArrivingIds] = useState(NO_IDS);
  const [previous, setPrevious] = useState<RowSnapshot>({ atNewest: !hasMore, rowIds });

  if (previous.rowIds !== rowIds || previous.atNewest === hasMore) {
    setPrevious({ atNewest: !hasMore, rowIds });
    if (
      enabled &&
      previous.atNewest &&
      classifyActivityRowTransition(previous.rowIds, rowIds) === 'append'
    )
      setArrivingIds(new Set(rowIds.slice(previous.rowIds.length)));
  }

  const onArrived = useCallback((id: string) => {
    setArrivingIds(current => {
      if (!current.has(id)) return current;
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }, []);

  return { arrivingIds, onArrived };
}
